// src/components/Till.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Minus,
  ShoppingCart,
  CreditCard,
  User,
  LogOut,
  Settings,
  X,
  Percent,
  RotateCcw,
  Clock,
  Monitor,
} from "lucide-react";
import type { MenuItem, OrderItem, PaymentMethod, Staff } from "../types";
import { supabase } from "../lib/supabase";
import SplitPayment from "./SplitPayment";
import PaymentScreen from "./PaymentScreen";
import TableManagement from "./TableManagement";
import FloorplanTablePicker from "./FloorplanTablePicker";
import { Capacitor } from "@capacitor/core";

// Web popup helpers (browser only)
import {
  openCustomerDisplay as openCustomerDisplayWin,
  sendToCustomerDisplay,
} from "../lib/customerDisplay";

// Native plugin (Android/iOS)
import { CustomerDisplay as NativeCustomerDisplay } from "../plugins/customerDisplay";

interface TillProps {
  currentStaff: Staff;
  storeId: string;
  onLogout: () => void;
}

interface Category {
  id: string;
  name: string;
  sort_order: number;
}

interface MealCustomization {
  menuItem: MenuItem;
  isMeal: boolean;
  selectedSide?: string;
  selectedDrink?: string;
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function money(n: number) {
  return `£${Number(n || 0).toFixed(2)}`;
}

const Till: React.FC<TillProps> = ({ currentStaff, storeId, onLogout }) => {
  const [currentView, setCurrentView] = useState<"pos" | "payments" | "tables">("pos");

  // Menu + categories
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | "uncat">("uncat");

  const [cart, setCart] = useState<OrderItem[]>([]);
  const [tableNumber, setTableNumber] = useState<string>("");
  const [customerName, setCustomerName] = useState<string>("");
  const [orderType, setOrderType] = useState<"dine_in" | "takeaway" | "delivery" | "collection">(
    "dine_in"
  );
  const [collectionTime, setCollectionTime] = useState<string>("");
  const [deliveryAddress, setDeliveryAddress] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("cash");

  const [showSplitPayment, setShowSplitPayment] = useState(false);
  const [discountPercent, setDiscountPercent] = useState<number>(0);

  const [showNumpad, setShowNumpad] = useState(false);
  const [numpadValue, setNumpadValue] = useState<string>("");
  const [numpadMode, setNumpadMode] = useState<"quantity" | "discount" | "table">("quantity");
  const [selectedCartItem, setSelectedCartItem] = useState<string | null>(null);

  const [paymentTiming, setPaymentTiming] = useState<"now" | "later">("now");

  const [showFloorplanPicker, setShowFloorplanPicker] = useState(false);
  const [showMealCustomization, setShowMealCustomization] = useState(false);
  const [mealCustomization, setMealCustomization] = useState<MealCustomization | null>(null);

  // Checkout modal
  const [showCheckout, setShowCheckout] = useState(false);

  // Web popup ref (only used in browser)
  const [customerDisplayWindow, setCustomerDisplayWindow] = useState<Window | null>(null);

  useEffect(() => {
    fetchMenuAndCategories();

    const ch = supabase
      .channel(`menu_items_${storeId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "menu_items", filter: `store_id=eq.${storeId}` },
        () => fetchMenuAndCategories()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  useEffect(() => {
    if (selectedCategoryId !== "uncat") return;
    const hasUncat = (menuItems as any[]).some((m) => m.category_id == null);
    if (!hasUncat && categories.length > 0) {
      setSelectedCategoryId(categories[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems, categories]);

  const fetchMenuAndCategories = async () => {
    try {
      const [itemsRes, catRes] = await Promise.all([
        supabase
          .from("menu_items")
          .select("id,store_id,name,price,category_id,created_at,updated_at")
          .eq("store_id", storeId)
          .order("name", { ascending: true }),

        supabase
          .from("menu_categories")
          .select("id,name,sort_order")
          .eq("store_id", storeId)
          .order("sort_order", { ascending: true })
          .order("name", { ascending: true }),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (catRes.error) throw catRes.error;

      setMenuItems((itemsRes.data ?? []) as MenuItem[]);
      setCategories((catRes.data ?? []) as Category[]);
    } catch (err) {
      console.error("Error fetching menu/categories:", err);
      setMenuItems([]);
      setCategories([]);
    }
  };

  const getSubtotal = () => {
    return cart.reduce((total, item) => {
      let itemPrice = item.menu_item?.price || 0;
      // @ts-ignore
      if (item.is_meal && item.menu_item?.meal_price_add) {
        // @ts-ignore
        itemPrice += item.menu_item.meal_price_add;
      }
      return total + itemPrice * item.quantity;
    }, 0);
  };

  const getDiscountAmount = () => getSubtotal() * (discountPercent / 100);
  const getTotalAmount = () => getSubtotal() - getDiscountAmount();

  const getPayload = () => ({
    cart,
    totalAmount: getTotalAmount(),
    customerName,
    tableNumber,
    orderType,
  });

  // Push state to customer display
  const broadcastCustomerDisplay = () => {
    const msg = {
      type: "UPDATE_ORDER",
      payload: getPayload(),
    };

    // Web popup
    sendToCustomerDisplay(msg as any, customerDisplayWindow);

    // Native fallback storage bus
    try {
      localStorage.setItem("forkcast:customer_display_state", JSON.stringify(msg));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    broadcastCustomerDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, customerName, tableNumber, orderType, discountPercent, customerDisplayWindow]);

  const addToCart = (menuItem: MenuItem) => {
    // @ts-ignore
    if (menuItem.can_be_meal) {
      setMealCustomization({
        menuItem,
        isMeal: false,
        selectedSide: undefined,
        selectedDrink: undefined,
      });
      setShowMealCustomization(true);
      return;
    }

    const existingItem = cart.find((item) => item.menu_item_id === menuItem.id);
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.menu_item_id === menuItem.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          id: Date.now().toString(),
          menu_item_id: menuItem.id,
          quantity: 1,
          menu_item: menuItem,
        } as OrderItem,
      ]);
    }
  };

  const updateQuantity = (itemId: string, change: number) => {
    setCart(
      cart
        .map((item) => {
          if (item.id === itemId) {
            const newQuantity = item.quantity + change;
            return newQuantity <= 0 ? null : { ...item, quantity: newQuantity };
          }
          return item;
        })
        .filter(Boolean) as OrderItem[]
    );
  };

  const removeFromCart = (itemId: string) => setCart(cart.filter((item) => item.id !== itemId));

  const clearCart = async () => {
    setCart([]);
    setDiscountPercent(0);
    setTableNumber("");
    setCustomerName("");
    setCollectionTime("");
    setDeliveryAddress("");
    setPaymentTiming("now");

    sendToCustomerDisplay({ type: "CLEAR_ORDER" } as any, customerDisplayWindow);

    try {
      localStorage.setItem(
        "forkcast:customer_display_state",
        JSON.stringify({ type: "CLEAR_ORDER" })
      );
    } catch {
      // ignore
    }
  };

  const handleNumpadClick = (value: string) => {
    if (value === "clear") {
      setNumpadValue("");
    } else if (value === "enter") {
      if (numpadMode === "quantity" && selectedCartItem) {
        const quantity = parseInt(numpadValue) || 1;
        setCart(
          cart.map((item) =>
            item.id === selectedCartItem ? { ...item, quantity: Math.max(1, quantity) } : item
          )
        );
      } else if (numpadMode === "discount") {
        setDiscountPercent(Math.min(100, Math.max(0, parseFloat(numpadValue) || 0)));
      } else if (numpadMode === "table") {
        setTableNumber(numpadValue);
      }
      setShowNumpad(false);
      setNumpadValue("");
      setSelectedCartItem(null);
    } else if (value === "backspace") {
      setNumpadValue(numpadValue.slice(0, -1));
    } else {
      if (numpadValue.length < 10) setNumpadValue(numpadValue + value);
    }
  };

  const openNumpad = (mode: "quantity" | "discount" | "table", itemId?: string) => {
    setNumpadMode(mode);
    setSelectedCartItem(itemId || null);
    setNumpadValue("");
    setShowNumpad(true);
  };

  const openCustomerDisplay = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const displays = await NativeCustomerDisplay.getDisplays();
        if (!displays || (Array.isArray(displays) && displays.length === 0)) {
          alert("No presentation-capable customer display detected (device may only mirror).");
          return;
        }

        await NativeCustomerDisplay.open({ path: "/customer-display" });

        const msg = { type: "UPDATE_ORDER", payload: getPayload() };
        try {
          localStorage.setItem("forkcast:customer_display_state", JSON.stringify(msg));
        } catch {
          // ignore
        }
      } catch (e: any) {
        alert("Customer display failed: " + String(e?.message ?? e));
      }
      return;
    }

    const w = openCustomerDisplayWin();
    if (!w) {
      alert("Please allow popups for this site to open the customer display");
      return;
    }
    setCustomerDisplayWindow(w);

    const send = () =>
      sendToCustomerDisplay({ type: "UPDATE_ORDER", payload: getPayload() } as any, w);
    setTimeout(send, 150);
    setTimeout(send, 600);
    setTimeout(send, 1500);
  };

  const createSale = async (payment: string) => {
    const total = getTotalAmount();

    const { data: sale, error: saleErr } = await supabase
      .from("sales")
      .insert([
        {
          store_id: storeId,
          type: orderType,
          payment,
          total,
        },
      ])
      .select("id")
      .single();

    if (saleErr || !sale?.id) {
      console.error("Error creating sale:", saleErr);
      throw new Error("Failed to create sale");
    }

    const saleId: number = sale.id;

    const itemsPayload = cart.map((ci) => {
      const unitPrice = Number(ci.menu_item?.price ?? 0);
      return {
        stores_id: storeId,
        sale_id: saleId,
        item_id: ci.menu_item_id,
        item_name: ci.menu_item?.name ?? "Item",
        quantity: ci.quantity,
        price: unitPrice,
      };
    });

    const { error: itemsErr } = await supabase.from("sales_items").insert(itemsPayload);

    if (itemsErr) {
      console.error("Error creating sales_items:", itemsErr);
      await supabase.from("sales").delete().eq("id", saleId).eq("store_id", storeId);
      throw new Error("Failed to create sale items");
    }

    return saleId;
  };

  const processOrderPayNow = async (payment: "cash" | "card") => {
    if (cart.length === 0) return;

    try {
      setPaymentMethod(payment);
      const saleId = await createSale(payment);
      alert(`Sale #${saleId} completed successfully!`);
      await clearCart();
    } catch (error) {
      console.error("processOrderPayNow error:", error);
      alert("Error completing sale. Please try again.");
    }
  };

  const processOrderPayLater = async () => {
    if (cart.length === 0) return;

    try {
      const saleId = await createSale("pending");
      alert(`Order sent. Payment pending. Sale #${saleId}`);
      await clearCart();
    } catch (error) {
      console.error("processOrderPayLater error:", error);
      alert("Error creating pay-later sale. Please try again.");
    }
  };

  const handleSplitPaymentComplete = async (_payments: PaymentMethod[], change: number) => {
    if (cart.length === 0) return;

    try {
      const saleId = await createSale("split");
      if (change > 0) {
        alert(`Sale #${saleId} completed! Change to give: £${change.toFixed(2)}`);
      } else {
        alert(`Sale #${saleId} completed!`);
      }
      await clearCart();
      setShowSplitPayment(false);
    } catch (error) {
      console.error("split payment error:", error);
      alert("Error completing split payment sale. Please try again.");
    }
  };

  const filteredMenuItems = useMemo(() => {
    if (selectedCategoryId === "uncat") {
      return (menuItems as any[]).filter((m) => m.category_id == null) as MenuItem[];
    }
    return (menuItems as any[]).filter((m) => m.category_id === selectedCategoryId) as MenuItem[];
  }, [menuItems, selectedCategoryId]);

  // Views
  if (currentView === "payments") {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <PaymentScreen
          currentStaff={currentStaff}
          storeId={storeId}
          setCurrentView={setCurrentView}
          onLogout={onLogout}
        />
      </div>
    );
  }

  if (currentView === "tables") {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <TableManagement
          currentStaff={currentStaff}
          setCurrentView={setCurrentView}
          onLogout={onLogout}
        />
      </div>
    );
  }

  const hasItems = cart.length > 0;

  return (
    <>
      <div className="h-screen bg-slate-50 text-slate-900 overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-bold shrink-0">
                  F
                </div>
                <div className="min-w-0">
                  <div className="text-base sm:text-lg font-semibold tracking-tight truncate">
                    Forkcast POS
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {orderType.replace("_", " ").toUpperCase()}
                    {tableNumber ? ` • Table ${tableNumber}` : ""}
                    {customerName ? ` • ${customerName}` : ""}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Mode tabs */}
                <div className="hidden lg:flex items-center gap-2 rounded-2xl bg-slate-100 p-1 ring-1 ring-slate-200">
                  <button
                    onClick={() => setCurrentView("pos")}
                    className={cx(
                      "px-3 py-2 rounded-xl text-sm font-semibold transition",
                      currentView === "pos"
                        ? "bg-white shadow-sm ring-1 ring-slate-200"
                        : "text-slate-600 hover:text-slate-900"
                    )}
                  >
                    POS
                  </button>
                  <button
                    onClick={() => setCurrentView("payments")}
                    className={cx(
                      "px-3 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2",
                      currentView === "payments"
                        ? "bg-white shadow-sm ring-1 ring-slate-200"
                        : "text-slate-600 hover:text-slate-900"
                    )}
                  >
                    <CreditCard size={16} />
                    Payments
                  </button>
                  <button
                    onClick={() => setCurrentView("tables")}
                    className={cx(
                      "px-3 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2",
                      currentView === "tables"
                        ? "bg-white shadow-sm ring-1 ring-slate-200"
                        : "text-slate-600 hover:text-slate-900"
                    )}
                  >
                    <Settings size={16} />
                    Tables
                  </button>
                </div>

                {/* Customer display */}
                <button
                  onClick={openCustomerDisplay}
                  className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 active:bg-violet-800 transition shadow-sm"
                  title="Open customer display"
                >
                  <Monitor size={16} />
                  <span className="hidden sm:inline">Customer Display</span>
                </button>

                {/* Staff */}
                <div className="hidden md:flex items-center gap-2 rounded-2xl bg-white ring-1 ring-slate-200 px-3 py-2">
                  <User size={16} className="text-slate-500" />
                  <div className="text-xs leading-tight">
                    <div className="font-semibold">{currentStaff.name}</div>
                    <div className="text-slate-500 capitalize">{currentStaff.role}</div>
                  </div>
                </div>

                <button
                  onClick={onLogout}
                  className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 transition shadow-sm"
                >
                  <LogOut size={16} />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            </div>

            {/* Mobile mode row */}
            <div className="lg:hidden mt-3 flex items-center gap-2">
              <button
                onClick={() => setCurrentView("pos")}
                className={cx(
                  "flex-1 px-3 py-2 rounded-xl text-sm font-semibold transition ring-1 ring-slate-200",
                  currentView === "pos"
                    ? "bg-slate-900 text-white ring-slate-900"
                    : "bg-white hover:bg-slate-50"
                )}
              >
                POS
              </button>
              <button
                onClick={() => setCurrentView("payments")}
                className={cx(
                  "flex-1 px-3 py-2 rounded-xl text-sm font-semibold transition ring-1 ring-slate-200 inline-flex items-center justify-center gap-2",
                  currentView === "payments"
                    ? "bg-slate-900 text-white ring-slate-900"
                    : "bg-white hover:bg-slate-50"
                )}
              >
                <CreditCard size={16} />
                Payments
              </button>
              <button
                onClick={() => setCurrentView("tables")}
                className={cx(
                  "flex-1 px-3 py-2 rounded-xl text-sm font-semibold transition ring-1 ring-slate-200 inline-flex items-center justify-center gap-2",
                  currentView === "tables"
                    ? "bg-slate-900 text-white ring-slate-900"
                    : "bg-white hover:bg-slate-50"
                )}
              >
                <Settings size={16} />
                Tables
              </button>
            </div>
          </div>
        </div>

        {/* Layout */}
        <div className="grid h-[calc(100vh-92px)] lg:h-[calc(100vh-76px)] grid-cols-1 lg:grid-cols-[minmax(640px,1fr)_560px] gap-0">
          {/* Left - Menu */}
          <div className="min-w-0 min-h-0 p-4">
            <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 h-full min-h-0 flex flex-col">
              {/* Categories */}
              <div className="px-4 pt-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="text-sm font-semibold text-slate-700">Menu</div>
                  <div className="text-xs text-slate-500">
                    {filteredMenuItems.length} item{filteredMenuItems.length === 1 ? "" : "s"}
                  </div>
                </div>

                <div className="flex items-center gap-2 overflow-x-auto pb-2">
                  <button
                    onClick={() => setSelectedCategoryId("uncat")}
                    className={cx(
                      "shrink-0 px-3 py-2 rounded-xl text-sm font-semibold ring-1 transition",
                      selectedCategoryId === "uncat"
                        ? "bg-slate-900 text-white ring-slate-900"
                        : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100"
                    )}
                  >
                    All / Uncategorised
                  </button>

                  {categories.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedCategoryId(c.id)}
                      className={cx(
                        "shrink-0 px-3 py-2 rounded-xl text-sm font-semibold ring-1 transition",
                        selectedCategoryId === c.id
                          ? "bg-slate-900 text-white ring-slate-900"
                          : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100"
                      )}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid */}
              <div className="px-4 pb-4 pt-2 overflow-hidden flex-1 min-h-0">
                <div className="h-full overflow-y-auto pr-1 p-1">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                    {filteredMenuItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => addToCart(item)}
                        className={cx(
                          "group rounded-2xl p-3 text-left ring-1 ring-slate-200 bg-white hover:bg-slate-50",
                          "active:bg-slate-100 transition shadow-sm hover:shadow",
                          "min-h-[92px] flex flex-col justify-between touch-manipulation"
                        )}
                      >
                        <div className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">
                          {item.name}
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="text-sm font-bold text-emerald-700">
                            {money(Number(item.price))}
                          </div>
                          <div className="text-xs font-semibold text-slate-500 opacity-0 group-hover:opacity-100 transition">
                            Add
                          </div>
                        </div>
                      </button>
                    ))}

                    {filteredMenuItems.length === 0 && (
                      <div className="col-span-full">
                        <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-6 text-center">
                          <div className="text-sm font-semibold text-slate-800">No items found</div>
                          <div className="text-sm text-slate-600 mt-1">Try another category.</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Order */}
          <div className="hidden lg:flex min-h-0 p-4 pl-0">
            <div className="w-[560px] max-w-[560px] h-full min-h-0 rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 w-full flex flex-col overflow-hidden">
              {/* Order header */}
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-slate-500">Current Order</div>
                    <div className="text-xl font-bold flex items-center gap-2">
                      <ShoppingCart size={20} />
                      {hasItems ? `${cart.length} item${cart.length === 1 ? "" : "s"}` : "Empty"}
                    </div>
                  </div>

                  <button
                    onClick={() => clearCart().catch(() => {})}
                    className="inline-flex items-center justify-center rounded-xl p-2 ring-1 ring-slate-200 bg-white hover:bg-slate-50 active:bg-slate-100 transition"
                    title="Clear order"
                  >
                    <RotateCcw size={18} className="text-slate-700" />
                  </button>
                </div>

                {/* Order type (compact dropdown) */}
                <div className="mt-4 flex items-end gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-slate-500">Order type</label>
                    <select
                      value={orderType}
                      onChange={(e) => setOrderType(e.target.value as any)}
                      className="mt-1 w-full bg-white ring-1 ring-slate-200 rounded-xl px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                    >
                      <option value="dine_in">Dine In</option>
                      <option value="takeaway">Takeaway</option>
                      <option value="collection">Collection</option>
                      <option value="delivery">Delivery</option>
                    </select>
                  </div>

                  {orderType === "dine_in" && (
                    <button
                      onClick={() => setShowFloorplanPicker(true)}
                      className="shrink-0 rounded-xl px-3 py-2 bg-slate-50 ring-1 ring-slate-200 hover:bg-slate-100 transition text-sm font-semibold"
                      title="Pick a table"
                    >
                      {tableNumber ? `Table ${tableNumber}` : "Pick table"}
                    </button>
                  )}
                </div>

                {/* Details row */}
                <div className="mt-3 grid grid-cols-1 gap-2">
                  {orderType === "dine_in" && (
                    <button
                      onClick={() => setShowFloorplanPicker(true)}
                      className="w-full p-2 rounded-xl bg-slate-50 ring-1 ring-slate-200 hover:bg-slate-100 transition text-left"
                    >
                      <div className="text-xs text-slate-500">Table</div>
                      <div className="font-semibold text-slate-900">
                        {tableNumber ? `Table ${tableNumber}` : "Pick a table"}
                      </div>
                    </button>
                  )}

                  {orderType === "collection" && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-2">
                        <label className="text-xs text-slate-500">Collection time</label>
                        <input
                          type="time"
                          value={collectionTime}
                          onChange={(e) => setCollectionTime(e.target.value)}
                          className="mt-1 w-full bg-white ring-1 ring-slate-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                        />
                      </div>
                      <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-2">
                        <label className="text-xs text-slate-500">Customer</label>
                        <input
                          type="text"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          className="mt-1 w-full bg-white ring-1 ring-slate-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                          placeholder="Name"
                        />
                      </div>
                    </div>
                  )}

                  {orderType === "delivery" && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-2">
                        <label className="text-xs text-slate-500">Address</label>
                        <textarea
                          value={deliveryAddress}
                          onChange={(e) => setDeliveryAddress(e.target.value)}
                          className="mt-1 w-full bg-white ring-1 ring-slate-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 resize-none"
                          placeholder="Delivery address"
                          rows={2}
                        />
                      </div>
                      <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-2">
                        <label className="text-xs text-slate-500">Customer</label>
                        <input
                          type="text"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          className="mt-1 w-full bg-white ring-1 ring-slate-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                          placeholder="Name"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Cart list (scrolls properly) */}
              <div className="flex-1 min-h-0 overflow-y-auto p-4">
                {!hasItems ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center">
                        <ShoppingCart size={22} className="text-slate-500" />
                      </div>
                      <div className="mt-3 font-semibold">No items in order</div>
                      <div className="text-sm text-slate-500 mt-1">Tap a menu item to add it.</div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cart.map((item) => {
                      const lineTotal = (item.menu_item?.price || 0) * item.quantity;
                      return (
                        <div key={item.id} className="rounded-xl bg-white ring-1 ring-slate-200 shadow-sm p-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate">
                                {item.menu_item?.name}
                              </div>
                              <div className="text-xs font-bold text-emerald-700">
                                {money(lineTotal)}
                              </div>
                            </div>

                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="rounded-lg p-1.5 ring-1 ring-slate-200 bg-white hover:bg-slate-50 active:bg-slate-100 transition"
                              title="Remove item"
                            >
                              <X size={14} className="text-rose-600" />
                            </button>
                          </div>

                          <div className="mt-2 flex items-center justify-between">
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                onClick={() => updateQuantity(item.id, -1)}
                                className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 transition shadow-sm"
                                title="Decrease"
                              >
                                <Minus size={14} />
                              </button>

                              <button
                                onClick={() => openNumpad("quantity", item.id)}
                                className="h-9 min-w-[44px] px-2 inline-flex items-center justify-center rounded-lg bg-slate-900 text-white font-bold hover:bg-slate-800 active:bg-slate-950 transition"
                                title="Set quantity"
                              >
                                {item.quantity}
                              </button>

                              <button
                                onClick={() => updateQuantity(item.id, 1)}
                                className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 transition shadow-sm"
                                title="Increase"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Totals + Checkout */}
              <div className="border-t border-slate-200 bg-slate-50 p-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-semibold">{money(getSubtotal())}</span>
                  </div>

                  {discountPercent > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Discount ({discountPercent}%)</span>
                      <span className="font-semibold text-amber-700">-{money(getDiscountAmount())}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                    <span className="text-base font-semibold">Total</span>
                    <span className="text-2xl font-extrabold text-emerald-700">
                      {money(getTotalAmount())}
                    </span>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => openNumpad("discount")}
                    className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-white ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition font-semibold"
                  >
                    <Percent size={16} className="text-amber-700" />
                    Discount
                  </button>

                  <button
                    onClick={() => setShowCheckout(true)}
                    disabled={!hasItems}
                    className={cx(
                      "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 transition font-semibold text-white shadow-sm",
                      hasItems
                        ? "bg-violet-600 hover:bg-violet-700 active:bg-violet-800"
                        : "bg-slate-300 cursor-not-allowed"
                    )}
                  >
                    <CreditCard size={16} />
                    Checkout
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Small screens: bottom bar */}
          <div className="lg:hidden w-full fixed bottom-0 left-0 right-0 z-20">
            <div className="border-t border-slate-200 bg-white/95 backdrop-blur p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <div className="text-slate-500">Total</div>
                  <div className="text-xl font-extrabold text-emerald-700">{money(getTotalAmount())}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openNumpad("discount")}
                    className="rounded-xl px-3 py-2 bg-white ring-1 ring-slate-200 font-semibold hover:bg-slate-50"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Percent size={16} className="text-amber-700" />
                      Discount
                    </span>
                  </button>
                  <button
                    onClick={() => setShowCheckout(true)}
                    disabled={!hasItems}
                    className={cx(
                      "rounded-xl px-3 py-2 font-semibold text-white shadow-sm",
                      hasItems ? "bg-violet-600 hover:bg-violet-700" : "bg-slate-300 cursor-not-allowed"
                    )}
                  >
                    Checkout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Numpad Modal */}
        {showNumpad && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-slate-500">
                      {numpadMode === "quantity"
                        ? "Quantity"
                        : numpadMode === "discount"
                        ? "Discount %"
                        : "Table number"}
                    </div>
                    <div className="text-lg font-bold">
                      {numpadMode === "quantity"
                        ? "Enter Quantity"
                        : numpadMode === "discount"
                        ? "Enter Discount"
                        : "Enter Table"}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowNumpad(false)}
                    className="rounded-xl p-2 ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition"
                    title="Close"
                  >
                    <X size={18} className="text-slate-700" />
                  </button>
                </div>

                <div className="mt-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-center">
                  <div className="text-3xl font-extrabold tracking-tight text-slate-900">
                    {numpadValue || "0"}
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-3 gap-3">
                  {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "backspace"].map((key) => (
                    <button
                      key={key}
                      onClick={() => handleNumpadClick(key)}
                      className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 hover:bg-slate-100 active:bg-slate-200 transition font-bold h-14"
                    >
                      {key === "backspace" ? "⌫" : key}
                    </button>
                  ))}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleNumpadClick("clear")}
                    className="rounded-2xl bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 transition font-bold h-12"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => handleNumpadClick("enter")}
                    className="rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 transition font-bold h-12"
                  >
                    Enter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Checkout Modal */}
        {showCheckout && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-sm text-slate-500">Checkout</div>
                  <div className="text-lg font-bold">Total: {money(getTotalAmount())}</div>
                </div>
                <button
                  onClick={() => setShowCheckout(false)}
                  className="rounded-xl p-2 ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition"
                  title="Close"
                >
                  <X size={18} className="text-slate-700" />
                </button>
              </div>

              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setPaymentTiming("now")}
                    className={cx(
                      "rounded-xl px-3 py-2 font-semibold ring-1 transition",
                      paymentTiming === "now"
                        ? "bg-slate-900 text-white ring-slate-900"
                        : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                    )}
                  >
                    Pay Now
                  </button>
                  <button
                    onClick={() => setPaymentTiming("later")}
                    className={cx(
                      "rounded-xl px-3 py-2 font-semibold ring-1 transition",
                      paymentTiming === "later"
                        ? "bg-slate-900 text-white ring-slate-900"
                        : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                    )}
                  >
                    Pay Later
                  </button>
                </div>

                {paymentTiming === "now" ? (
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => {
                        setShowCheckout(false);
                        processOrderPayNow("cash");
                      }}
                      disabled={!hasItems}
                      className={cx(
                        "rounded-xl px-3 py-3 font-bold text-white transition shadow-sm",
                        hasItems
                          ? "bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800"
                          : "bg-slate-300 cursor-not-allowed"
                      )}
                    >
                      Cash
                    </button>
                    <button
                      onClick={() => {
                        setShowCheckout(false);
                        processOrderPayNow("card");
                      }}
                      disabled={!hasItems}
                      className={cx(
                        "rounded-xl px-3 py-3 font-bold text-white transition shadow-sm",
                        hasItems
                          ? "bg-slate-900 hover:bg-slate-800 active:bg-slate-950"
                          : "bg-slate-300 cursor-not-allowed"
                      )}
                    >
                      Card
                    </button>
                    <button
                      onClick={() => {
                        setShowCheckout(false);
                        setShowSplitPayment(true);
                      }}
                      disabled={!hasItems}
                      className={cx(
                        "rounded-xl px-3 py-3 font-bold text-white transition shadow-sm",
                        hasItems
                          ? "bg-slate-900 hover:bg-slate-800 active:bg-slate-950"
                          : "bg-slate-300 cursor-not-allowed"
                      )}
                    >
                      Split
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setShowCheckout(false);
                      processOrderPayLater();
                    }}
                    disabled={!hasItems}
                    className={cx(
                      "w-full rounded-xl px-4 py-3 font-bold text-white transition shadow-sm",
                      hasItems
                        ? "bg-amber-600 hover:bg-amber-700 active:bg-amber-800"
                        : "bg-slate-300 cursor-not-allowed"
                    )}
                  >
                    Send Order (Pay Later)
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showSplitPayment && (
        <SplitPayment
          totalAmount={getTotalAmount()}
          onComplete={handleSplitPaymentComplete}
          onCancel={() => setShowSplitPayment(false)}
        />
      )}

      {showFloorplanPicker && (
        <FloorplanTablePicker
          onTableSelect={(table) => {
            // @ts-ignore
            if (table.table_number === 0) setTableNumber("");
            // @ts-ignore
            else setTableNumber(table.table_number.toString());
            setShowFloorplanPicker(false);
          }}
          onClose={() => setShowFloorplanPicker(false)}
        />
      )}
    </>
  );
};

export default Till;
